import { memo, useState } from 'react';
import { FieldError, UseFormRegister } from 'react-hook-form';
import styled from 'styled-components';
import NextImage from 'next/image';
import cx from 'classnames';

import { Fields, Names } from 'types/fields';
import Text from '../text';
import { TextFieldError, TextFieldInput, TextFieldLabel } from '../text-field';

export type TextFieldPasswordProps = {
  type: 'password';
  label?: string;
  name: Names;
  register: UseFormRegister<Fields>;
  error?: FieldError;
  placeholder?: string;
  initialValue?: string;
};

function TextFieldPassword({
  type,
  label,
  name,
  register,
  error,
  placeholder,
  initialValue,
}: TextFieldPasswordProps) {
  const [hide, setHide] = useState(true);

  function togglePasswordVisibility() {
    setHide((prev) => !prev);
  }

  return (
    <TextFieldPasswordWrapper>
      {label && (
        <TextFieldLabel data-testid="password-field-label">
          <label htmlFor={name}>
            <Text type="mode2" fw="light">
              {label}
            </Text>
          </label>
        </TextFieldLabel>
      )}

      <TextFieldInput className="input-wrapper">
        <input
          defaultValue={initialValue}
          type={hide ? type : 'text'}
          id={name}
          data-testid="password-field"
          placeholder={placeholder}
          {...register(name)}
          className={cx({ invalid: Boolean(error) })}
        />
        <div
          className="icon"
          onClick={togglePasswordVisibility}
          data-testid="icon"
        >
          {hide ? (
            <NextImage
              src="/static/icons/eye.svg"
              width={24}
              height={24}
              data-testid="show-icon"
            />
          ) : (
            <NextImage
              src="/static/icons/eye-close.svg"
              width={24}
              height={24}
              data-testid="hide-icon"
            />
          )}
        </div>
      </TextFieldInput>

      <TextFieldError>
        {error?.message && (
          <Text type="mode2" fw="medium" data-testid="password-field-error">
            {error.message}
          </Text>
        )}
      </TextFieldError>
    </TextFieldPasswordWrapper>
  );
}

const TextFieldPasswordWrapper = styled.div`
  margin-top: 24px;
  .input-wrapper {
    position: relative;
    .icon {
      cursor: pointer;
      display: flex;
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 24px;
    }
  }
`;

export default memo(TextFieldPassword);
